import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import {
  Shield,
  Target,
  Users,
  Lightbulb,
  Heart,
  ArrowRight,
  CheckCircle2,
} from 'lucide-react';

export const AboutPage = () => {
  const values = [
    {
      icon: Shield,
      title: 'Compliance First',
      description: 'HIPAA compliance is not an afterthought—it\'s built into every feature we develop.',
    },
    {
      icon: Lightbulb,
      title: 'Innovation',
      description: 'We leverage cutting-edge technology like AI to solve real problems for DME providers.',
    },
    {
      icon: Users,
      title: 'Customer Success',
      description: 'Your success is our success. We\'re partners in your digital transformation journey.',
    },
    {
      icon: Heart,
      title: 'Healthcare Impact',
      description: 'We\'re driven by the impact our software has on patient care and outcomes.',
    },
  ];

  const milestones = [
    { year: '2020', event: 'Founded with a mission to modernize DME management' },
    { year: '2021', event: 'Launched beta with 10 early adopter DME providers' },
    { year: '2022', event: 'Achieved full HIPAA compliance certification' },
    { year: '2023', event: 'Expanded to 10 major supplier integrations' },
    { year: '2024', event: 'Introduced AI-powered features and SEO generator' },
    { year: '2025', event: 'Serving 500+ DME providers nationwide' },
  ];

  return (
    <div className="min-h-screen pt-48">
      {/* Hero */}
      <section className="py-20 px-6 lg:px-8 bg-gradient-to-br from-slate-50 to-slate-100">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-6">
                Built by Healthcare Professionals, for Healthcare Professionals
              </h1>
              <p className="text-lg text-slate-600 leading-relaxed mb-8">
                DME R'US was founded with a simple mission: to provide DME providers with 
                enterprise-grade tools without the enterprise price tag. We understand the 
                unique challenges of durable medical equipment management because we've 
                lived them.
              </p>
              <Link to="/contact">
                <Button
                  data-testid="about-contact-btn"
                  className="bg-slate-900 text-white hover:bg-slate-800 rounded-md px-8 py-4"
                >
                  Get In Touch
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </div>
            <div>
              <img
                src="https://images.unsplash.com/photo-1631039302217-5a6c56371e86?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwxfHxoZWFsdGhjYXJlJTIwdGVhbSUyMG1lZXRpbmclMjBwcm9mZXNzaW9uYWx8ZW58MHx8fHwxNzcwMjQ3Mzg3fDA&ixlib=rb-4.1.0&q=85"
                alt="Healthcare team meeting"
                className="rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Mission */}
      <section className="py-24 px-6 lg:px-8 bg-white">
        <div className="max-w-4xl mx-auto text-center">
          <div className="w-16 h-16 bg-teal-50 rounded-2xl flex items-center justify-center mx-auto mb-8">
            <Target className="w-8 h-8 text-teal-600" />
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-6">Our Mission</h2>
          <p className="text-xl text-slate-600 leading-relaxed">
            To empower durable medical equipment providers with modern, HIPAA-compliant 
            technology that streamlines operations, ensures compliance, and ultimately 
            improves patient care. We believe every DME provider deserves access to 
            enterprise-grade tools, regardless of size.
          </p>
        </div>
      </section>

      {/* Values */}
      <section data-testid="values-section" className="py-24 px-6 lg:px-8 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">Our Values</h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              The principles that guide everything we do.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <div
                key={index}
                className="bg-white border border-slate-200 rounded-2xl p-8 text-center hover:shadow-lg transition-all"
              >
                <div className="w-14 h-14 bg-teal-50 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <value.icon className="w-7 h-7 text-teal-600" />
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-3">{value.title}</h3>
                <p className="text-slate-600 leading-relaxed">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-24 px-6 lg:px-8 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">Our Journey</h2>
            <p className="text-lg text-slate-600">
              From startup to trusted DME platform.
            </p>
          </div>

          <div className="space-y-8">
            {milestones.map((milestone, index) => (
              <div key={index} className="flex items-start gap-6">
                <div className="flex-shrink-0 w-20 text-right">
                  <span className="text-2xl font-bold text-teal-600">{milestone.year}</span>
                </div>
                <div className="flex-shrink-0 w-4 h-4 bg-teal-600 rounded-full mt-2" />
                <div className="flex-1 pb-8 border-b border-slate-200 last:border-0">
                  <p className="text-lg text-slate-700">{milestone.event}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-24 px-6 lg:px-8 bg-slate-900">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
                Why Choose DME R'US?
              </h2>
              <ul className="space-y-4">
                {[
                  'Purpose-built for DME providers, not generic healthcare',
                  'Full HIPAA compliance without the complexity',
                  'AI-powered features that save hours of work',
                  'Integrated with 10 major DME suppliers',
                  '34,477 organically generated location pages for more than 50 US states',
                  'Dedicated support from healthcare technology experts',
                ].map((item, index) => (
                  <li key={index} className="flex items-center gap-3">
                    <CheckCircle2 className="w-5 h-5 text-teal-400 flex-shrink-0" />
                    <span className="text-slate-300">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="text-center">
              <img
                src="https://images.unsplash.com/photo-1651326659056-f03abf637022?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjB3aGVlbGNoYWlyJTIwbWVkaWNhbCUyMGVxdWlwbWVudCUyMHN0dWRpb3xlbnwwfHx8fDE3NzAyNDc0MDN8MA&ixlib=rb-4.1.0&q=85"
                alt="Modern medical equipment"
                className="rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 px-6 lg:px-8 bg-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-6">
            Ready to Join Us?
          </h2>
          <p className="text-lg text-slate-600 mb-10">
            Let's discuss how DME R'US can help transform your DME business.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/demo">
              <Button
                data-testid="about-demo-btn"
                className="bg-slate-900 text-white hover:bg-slate-800 rounded-md px-8 py-4 text-lg"
              >
                Request Demo
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <Link to="/contact">
              <Button
                data-testid="about-cta-contact-btn"
                variant="outline"
                className="border-slate-300 hover:bg-slate-50 rounded-md px-8 py-4 text-lg"
              >
                Contact Us
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
